import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import {
  TestimonialFormData,
  TestimonialStatus,
} from '@/types/testimonial.types';
import {
  useCreateTestimonialMutation,
  useGetTestimonialByIdQuery,
  useUpdateTestimonialMutation,
  useTranslateTestimonialContentMutation,
} from '@/services/TestimonialService';
import { ErrorApiResponse } from '@/types/api.types';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import { useIntl } from 'react-intl';
import TestimonialPreviewModal from './TestimonialPreviewModal';

interface TestimonialFormPageProps {
  mode: 'create' | 'edit' | 'view';
}

const testimonialSchema = Yup.object().shape({
  authorName: Yup.string().required(
    'TESTIMONIAL.VALIDATION.AUTHOR_NAME_REQUIRED'
  ),
  content: Yup.string().required('TESTIMONIAL.VALIDATION.CONTENT_REQUIRED'),
  authorTitle: Yup.string().max(120, 'TESTIMONIAL.VALIDATION.AUTHOR_TITLE_MAX'),
  status: Yup.mixed<TestimonialStatus>()
    .oneOf(
      [TestimonialStatus.DRAFT, TestimonialStatus.PUBLISHED],
      'TESTIMONIAL.VALIDATION.STATUS_INVALID'
    )
    .required('TESTIMONIAL.VALIDATION.STATUS_REQUIRED'),
});

const TestimonialFormPage = ({ mode }: TestimonialFormPageProps) => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { currentLanguage, changeLanguage } = useLanguage();
  const intl = useIntl();
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const testimonialId = isEdit || isView ? params.id : undefined;

  const [formData, setFormData] = useState<TestimonialFormData>({
    authorName: '',
    authorTitle: '',
    content: '',
    profileImage: null,
    status: TestimonialStatus.DRAFT,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isTranslatingPreview, setIsTranslatingPreview] = useState(false);

  const {
    data: testimonialDetail,
    isLoading: isDetailLoading,
    refetch: refetchTestimonial,
  } = useGetTestimonialByIdQuery(testimonialId as string, {
    skip: (!isEdit && !isView) || !testimonialId,
  });

  const [createTestimonial, { isLoading: isCreating }] =
    useCreateTestimonialMutation();
  const [updateTestimonial, { isLoading: isUpdating }] =
    useUpdateTestimonialMutation();
  const [translateTestimonialContent] =
    useTranslateTestimonialContentMutation();

  useEffect(() => {
    if (!testimonialId || (!isEdit && !isView)) return;
    void refetchTestimonial();
  }, [testimonialId, isEdit, isView, currentLanguage.code, refetchTestimonial]);

  useEffect(() => {
    if (testimonialDetail?.data && (isEdit || isView)) {
      const t = testimonialDetail.data;
      const image = t.profile_image_url ?? t.profile_image ?? null;
      setFormData({
        id: t.id,
        authorName: t.author_name,
        authorTitle: t.author_title ?? '',
        content: t.content,
        profileImage: image,
        status: t.status,
        translations: t.translations,
      });
      setPreviewUrl(image);
      setErrors({});
    }
  }, [testimonialDetail, isEdit, isView]);

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value } = e.target;
      setFormData((prev) => {
        if (name === 'status') {
          return {
            ...prev,
            status: Number(value) as TestimonialStatus,
          };
        }
        return { ...prev, [name]: value };
      });
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
    },
    [errors]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setFormData((prev) => ({ ...prev, profileImage: file }));
        setPreviewUrl(URL.createObjectURL(file));
        setErrors((prev) => ({ ...prev, profileImage: '' }));
      }
    },
    []
  );

  const handleRemoveImage = useCallback(() => {
    setFormData((prev) => ({ ...prev, profileImage: null }));
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const validateField = useCallback(
    async (field: keyof TestimonialFormData) => {
      try {
        await testimonialSchema.validateAt(field, formData);
        setErrors((prev) => ({ ...prev, [field]: '' }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({ ...prev, [field]: err.message }));
        }
      }
    },
    [formData]
  );

  const handleBlur = useCallback(
    (field: keyof TestimonialFormData) => {
      if (field === 'profileImage') return;
      if (isView) return;
      void validateField(field);
    },
    [isView, validateField]
  );
  const handleOpenPreview = useCallback(async () => {
    const content = formData.content ?? '';
    if (!content.trim()) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'TESTIMONIAL.VALIDATION.CONTENT_REQUIRED_FOR_PREVIEW',
        }),
        { variant: 'warning' }
      );
      return;
    }

    // If translations already exist, open preview directly
    const raw = formData.translations;
    const hasExisting =
      raw &&
      (typeof raw === 'string'
        ? raw.trim() !== '' && raw !== '{}' && raw !== 'null'
        : Object.keys(raw as object).length > 0);

    if (hasExisting) {
      setIsPreviewModalOpen(true);
      return;
    }

    try {
      setIsTranslatingPreview(true);
      const translatePayload = {
        testimonial_id: formData.id,
        content: formData.content ?? '',
        author_title: formData.authorTitle,
        source_language: currentLanguage.code || 'en',
      };

      const translateResponse = await translateTestimonialContent(translatePayload).unwrap();

      if (translateResponse.translations) {
        setFormData((prev) => ({
          ...prev,
          translations: translateResponse.translations,
        }));
      }
      setIsPreviewModalOpen(true);
    } catch {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'TESTIMONIAL.PREVIEW.TRANSLATION_ERROR',
        }),
        { variant: 'warning' }
      );
      // Still open preview modal so user can manually translate
      setIsPreviewModalOpen(true);
    } finally {
      setIsTranslatingPreview(false);
    }
  }, [formData.id, formData.content, formData.authorTitle, formData.translations, currentLanguage.code, translateTestimonialContent, enqueueSnackbar, intl]);
  const handleSubmit = useCallback(async () => {
    try {
      await testimonialSchema.validate(formData, {
        abortEarly: false,
      });

      let finalTranslations = formData.translations;
      let hasExistingTranslations = false;

      if (finalTranslations) {
        if (typeof finalTranslations === 'string') {
          const trimmed = finalTranslations.trim();
          hasExistingTranslations =
            trimmed !== '' && trimmed !== '{}' && trimmed !== 'null';
        } else if (typeof finalTranslations === 'object') {
          hasExistingTranslations =
            Object.keys(finalTranslations as Record<string, unknown>).length >
            0;
        }
      }

      if (!hasExistingTranslations) {
        try {
          const translatePayload = {
            testimonial_id: formData.id,
            content: formData.content ?? '',
            author_title: formData.authorTitle,
            source_language: currentLanguage.code || 'en',
          };

          const translateResponse =
            await translateTestimonialContent(translatePayload).unwrap();

          if (translateResponse.translations) {
            finalTranslations = translateResponse.translations;
            setFormData((prev) => ({
              ...prev,
              translations: translateResponse.translations,
            }));
          }
        } catch {
          // If translation fails, continue saving without translations
          enqueueSnackbar(
            intl.formatMessage({
              id: 'TESTIMONIAL.PREVIEW.TRANSLATION_ERROR',
            }),
            { variant: 'warning' }
          );
        }
      }

      const payload = new FormData();
      payload.append('author_name', formData.authorName);
      if (formData.authorTitle)
        payload.append('author_title', formData.authorTitle);
      payload.append('content', formData.content);
      payload.append('status', String(formData.status));

      if (formData.profileImage instanceof File) {
        payload.append('profile_image', formData.profileImage);
      } else if (
        isEdit &&
        testimonialDetail?.data &&
        (testimonialDetail.data.profile_image_url ||
          testimonialDetail.data.profile_image) &&
        !formData.profileImage
      ) {
        payload.append('profile_image', 'null');
      }

      if (finalTranslations) {
        const raw = finalTranslations;
        const value = typeof raw === 'string' ? raw : JSON.stringify(raw ?? {});
        if (value && value !== '{}') {
          payload.append('translations', value);
        }
      }

      if (isEdit && testimonialId) {
        await updateTestimonial({ id: testimonialId, body: payload }).unwrap();
        enqueueSnackbar('Testimonial updated successfully', {
          variant: 'success',
        });
      } else {
        await createTestimonial(payload).unwrap();
        enqueueSnackbar('Testimonial created successfully', {
          variant: 'success',
        });
      }
      navigate('/testimonial-management');
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach((e) => {
          if (e.path) validationErrors[e.path] = e.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar(intl.formatMessage({ id: 'TESTIMONIAL.ERROR.FORM' }), {
          variant: 'error',
        });
      } else {
        const apiError = err as ErrorApiResponse;
        const message =
          apiError?.data?.message ||
          intl.formatMessage({ id: 'TESTIMONIAL.ERROR.SAVE' });
        enqueueSnackbar(message, { variant: 'error' });
      }
    }
  }, [
    createTestimonial,
    enqueueSnackbar,
    formData,
    intl,
    isEdit,
    navigate,
    testimonialId,
    updateTestimonial,
    translateTestimonialContent,
    currentLanguage.code,
  ]);

  const isSubmitting = isCreating || isUpdating || isDetailLoading;

  const toolbarTitle = isEdit
    ? 'Edit Testimonial'
    : isView
      ? 'View Testimonial'
      : 'Add New Testimonial';
  const toolbarDescription = isEdit
    ? 'Update testimonial details'
    : isView
      ? 'View testimonial details'
      : 'Create a new testimonial';

  return (
    <>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text={toolbarTitle} />
              <ToolbarDescription>{toolbarDescription}</ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <div className="flex items-center gap-3 mr-4">
                <div className="relative">
                  <select
                    id="testimonial-language-select"
                    className="select select-bordered pl-8 pr-8 h-10 leading-normal"
                    value={currentLanguage.code}
                    onChange={(e) => {
                      const selectedLang = I18N_LANGUAGES.find(
                        (lang: { code: string; label: string }) =>
                          lang.code === e.target.value
                      );
                      if (selectedLang) {
                        changeLanguage(selectedLang);
                      }
                    }}
                    aria-label={intl.formatMessage({
                      id: 'TESTIMONIAL.FORM.SELECT_LANGUAGE',
                    })}
                  >
                    {I18N_LANGUAGES.map(
                      (lang: { code: string; label: string }) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.label}
                        </option>
                      )
                    )}
                  </select>
                  <img
                    src="/media/flags/globe.svg"
                    alt=""
                    className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                    aria-hidden="true"
                  />
                </div>
                {!isView &&
                  ((formData.authorName ?? '').trim() ||
                    (formData.content ?? '').trim()) && (
                    <button
                      type="button"
                      onClick={handleOpenPreview}
                      className="btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                      aria-label={intl.formatMessage({
                        id: 'COMMON.SHOW_PREVIEW',
                      })}
                      disabled={isSubmitting || isTranslatingPreview}
                    >
                      {isTranslatingPreview && (
                        <svg
                          className="animate-spin h-3.5 w-3.5 mr-1"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      )}
                      {isTranslatingPreview ? 'Preparing Preview…' : intl.formatMessage({ id: 'COMMON.SHOW_PREVIEW' })}
                    </button>
                  )}
                <button
                  type="button"
                  className="btn btn-sm btn-light h-10"
                  onClick={() => navigate('/testimonial-management')}
                  disabled={isSubmitting}
                >
                  {isView ? 'Back' : 'Cancel'}
                </button>
              </div>
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed">
        <div className="p-4">
          <div className="card-body">
            <div className="flex flex-col gap-6 max-w-2xl mx-auto">
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="authorName"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {intl.formatMessage({
                      id: 'TESTIMONIAL.FORM.AUTHOR_NAME',
                    })}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    id="authorName"
                    name="authorName"
                    type="text"
                    value={formData.authorName}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('authorName')}
                    disabled={isSubmitting || isView}
                    placeholder={intl.formatMessage({
                      id: 'TESTIMONIAL.FORM.AUTHOR_NAME.PLACEHOLDER',
                    })}
                    className={clsx(
                      'input input-bordered w-full h-10',
                      errors.authorName && 'border-red-500 focus:ring-red-500'
                    )}
                    aria-invalid={!!errors.authorName}
                    aria-describedby={
                      errors.authorName ? 'authorName-error' : undefined
                    }
                    autoComplete="name"
                  />
                  {errors.authorName && (
                    <p
                      id="authorName-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {intl.formatMessage({ id: errors.authorName })}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="authorTitle"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {intl.formatMessage({
                      id: 'TESTIMONIAL.FORM.AUTHOR_TITLE',
                    })}
                  </label>
                  <input
                    id="authorTitle"
                    name="authorTitle"
                    type="text"
                    value={formData.authorTitle || ''}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('authorTitle')}
                    disabled={isSubmitting || isView}
                    placeholder={intl.formatMessage({
                      id: 'TESTIMONIAL.FORM.AUTHOR_TITLE.PLACEHOLDER',
                    })}
                    className={clsx(
                      'input input-bordered w-full h-10',
                      errors.authorTitle && 'border-red-500 focus:ring-red-500'
                    )}
                    aria-invalid={!!errors.authorTitle}
                    aria-describedby={
                      errors.authorTitle ? 'authorTitle-error' : undefined
                    }
                    autoComplete="off"
                  />
                  {errors.authorTitle && (
                    <p
                      id="authorTitle-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {intl.formatMessage({ id: errors.authorTitle })}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="content"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {intl.formatMessage({ id: 'TESTIMONIAL.FORM.CONTENT' })}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('content')}
                    disabled={isSubmitting || isView}
                    placeholder={intl.formatMessage({
                      id: 'TESTIMONIAL.FORM.CONTENT.PLACEHOLDER',
                    })}
                    className={clsx(
                      'textarea textarea-bordered w-full h-28',
                      errors.content && 'border-red-500 focus:ring-red-500'
                    )}
                    aria-invalid={!!errors.content}
                    aria-describedby={
                      errors.content ? 'content-error' : undefined
                    }
                    autoComplete="off"
                  />
                  {errors.content && (
                    <p
                      id="content-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {intl.formatMessage({ id: errors.content })}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="profileImage"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {intl.formatMessage({
                      id: 'TESTIMONIAL.FORM.PROFILE_IMAGE',
                    })}
                  </label>
                  <input
                    ref={fileInputRef}
                    id="profileImage"
                    name="profileImage"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={isSubmitting || isView}
                    className={clsx(
                      'file-input file-input-bordered w-full h-10',
                      errors.profileImage && 'border-red-500 focus:ring-red-500'
                    )}
                    aria-invalid={!!errors.profileImage}
                    aria-describedby={
                      errors.profileImage ? 'profileImage-error' : undefined
                    }
                    autoComplete="off"
                  />
                  {errors.profileImage && (
                    <p
                      id="profileImage-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {intl.formatMessage({ id: errors.profileImage })}
                    </p>
                  )}
                  {previewUrl && (
                    <div className="mt-2 flex items-center gap-4">
                      <img
                        src={previewUrl}
                        alt="Profile preview"
                        className="w-20 h-20 object-cover rounded-full border"
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={handleRemoveImage}
                        disabled={isSubmitting || isView}
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="status"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {intl.formatMessage({ id: 'TESTIMONIAL.FORM.STATUS' })}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('status')}
                    disabled={isSubmitting || isView}
                    className={clsx(
                      'select select-bordered w-full h-10',
                      errors.status && 'border-red-500 focus:ring-red-500'
                    )}
                    aria-invalid={!!errors.status}
                    aria-describedby={
                      errors.status ? 'status-error' : undefined
                    }
                  >
                    <option value={TestimonialStatus.DRAFT}>Draft</option>
                    <option value={TestimonialStatus.PUBLISHED}>
                      Published
                    </option>
                  </select>
                  {errors.status && (
                    <p
                      id="status-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {intl.formatMessage({ id: errors.status })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            {!isView && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isEdit ? 'Save Changes' : 'Create'}
              </button>
            )}
          </div>
        </div>
      </div>

      <TestimonialPreviewModal
        open={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        formData={formData}
        source_language={currentLanguage.code}
        initialTranslations={
          typeof formData.translations === 'string'
            ? (() => {
              try {
                return JSON.parse(formData.translations);
              } catch {
                return {};
              }
            })()
            : formData.translations
        }
        onTranslationsUpdate={(t) =>
          setFormData((prev) => ({ ...prev, translations: t }))
        }
      />
    </>
  );
};

export default TestimonialFormPage;
